class AltoSearch extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector('[data-alto-search-input]');
    this.clearButton = this.querySelector('[data-alto-search-clear]');
    this.emptyState = this.querySelector('[data-alto-search-empty]');
    this.errorState = this.querySelector('[data-alto-search-error]');
    this.resultsContainer = this.querySelector('[data-alto-search-results]');
    this.statusEl = this.querySelector('[data-alto-search-status]');
    this.limit = parseInt(this.dataset.limit, 10) || 8;
    this.debounceMs = 300;
    this.debounceTimer = null;
    this.abortController = null;
    this.hasNoResults = false;

    this.onInput = this.onInput.bind(this);
    this.onClear = this.onClear.bind(this);
    this.onFormSubmit = this.onFormSubmit.bind(this);
  }

  connectedCallback() {
    if (!this.input) return;
    this.input.addEventListener('input', this.onInput);
    if (this.clearButton) this.clearButton.addEventListener('click', this.onClear);
    const form = this.querySelector('form');
    if (form) form.addEventListener('submit', this.onFormSubmit);

    this.modal = this.closest('modal-dialog');
    if (this.modal) {
      if (this.modalObserver) this.modalObserver.disconnect();
      // Observe our own modal's `open` attribute directly instead of the
      // global `modalClosed` body event — that event fires for every modal
      // (cart-notification, etc.) and would reset us when unrelated modals close.
      this.modalObserver = new MutationObserver(() => {
        if (this.modal.hasAttribute('open')) {
          this.syncHeaderTop();
          // Defer focus until after modal-dialog's trapFocus() runs and the
          // visibility/transform transition starts. Two rAFs is the reliable
          // pattern: first rAF waits for the style change to commit, second
          // waits for layout to settle so input.focus() actually takes.
          requestAnimationFrame(() => {
            requestAnimationFrame(() => this.input.focus());
          });
        } else {
          this.reset();
        }
      });
      this.modalObserver.observe(this.modal, { attributes: true, attributeFilter: ['open'] });
    }
  }

  disconnectedCallback() {
    if (this.modalObserver) this.modalObserver.disconnect();
  }

  syncHeaderTop() {
    const header = document.querySelector('.section-alto-header');
    if (!header) return;
    const top = Math.max(0, header.getBoundingClientRect().top);
    document.documentElement.style.setProperty('--alto-header-top', `${top}px`);
  }

  onInput() {
    const query = this.input.value.trim();
    this.toggleClearButton(query.length > 0);

    clearTimeout(this.debounceTimer);
    if (this.abortController) this.abortController.abort();

    if (query.length === 0) {
      this.showEmpty();
      return;
    }

    this.debounceTimer = setTimeout(() => this.fetchResults(query), this.debounceMs);
  }

  onClear(event) {
    event.preventDefault();
    this.input.value = '';
    this.input.focus();
    this.toggleClearButton(false);
    clearTimeout(this.debounceTimer);
    if (this.abortController) this.abortController.abort();
    this.showEmpty();
  }

  onFormSubmit(event) {
    if (!this.input.value.trim() || this.hasNoResults) event.preventDefault();
  }

  reset() {
    this.input.value = '';
    this.toggleClearButton(false);
    clearTimeout(this.debounceTimer);
    if (this.abortController) this.abortController.abort();
    this.showEmpty();
  }

  toggleClearButton(visible) {
    if (!this.clearButton) return;
    this.clearButton.hidden = !visible;
  }

  showEmpty() {
    if (this.emptyState) this.emptyState.hidden = false;
    if (this.errorState) this.errorState.hidden = true;
    if (this.resultsContainer) {
      this.resultsContainer.hidden = true;
      this.resultsContainer.innerHTML = '';
    }
    this.hasNoResults = false;
    this.setAttribute('data-state', 'empty');
    this.removeAttribute('aria-busy');
  }

  showResults(html) {
    if (this.emptyState) this.emptyState.hidden = true;
    if (this.errorState) this.errorState.hidden = true;
    if (this.resultsContainer) {
      this.resultsContainer.hidden = false;
      this.resultsContainer.innerHTML = html;
      this.hasNoResults = !!this.resultsContainer.querySelector('[data-alto-search-no-results]');
    }
    this.setAttribute('data-state', 'results');
    this.removeAttribute('aria-busy');
  }

  showError() {
    if (this.emptyState) this.emptyState.hidden = true;
    if (this.errorState) this.errorState.hidden = false;
    if (this.resultsContainer) {
      this.resultsContainer.hidden = true;
      this.resultsContainer.innerHTML = '';
    }
    this.hasNoResults = false;
    this.setAttribute('data-state', 'error');
    this.removeAttribute('aria-busy');
  }

  async fetchResults(query) {
    if (!window.routes || !window.routes.predictive_search_url) {
      console.error('alto-search: window.routes.predictive_search_url is not defined');
      return;
    }

    this.setAttribute('aria-busy', 'true');
    this.abortController = new AbortController();
    const signal = this.abortController.signal;

    const sectionUrl = new URL(window.routes.predictive_search_url, window.location.origin);
    sectionUrl.searchParams.set('q', query);
    sectionUrl.searchParams.set('section_id', 'alto-predictive-search');
    sectionUrl.searchParams.set('resources[type]', 'product,collection');
    sectionUrl.searchParams.set('resources[limit]', this.limit);
    sectionUrl.searchParams.set('resources[limit_scope]', 'each');

    // Fetch /search?section_id=alto-search-count to get the true total count
    // (Section Rendering API). /search/suggest caps at 10, so the "View all products (N)"
    // link would otherwise plateau at 10 for shops with more matches.
    const countUrl = new URL(
      window.routes.predictive_search_url.replace(/\/suggest$/, ''),
      window.location.origin
    );
    countUrl.searchParams.set('q', query);
    countUrl.searchParams.set('type', 'product');
    countUrl.searchParams.set('section_id', 'alto-search-count');

    try {
      const [sectionResult, countResult] = await Promise.allSettled([
        fetch(sectionUrl.toString(), { signal }),
        fetch(countUrl.toString(), { signal }),
      ]);

      if (sectionResult.status === 'rejected') throw sectionResult.reason;
      if (!sectionResult.value.ok) throw new Error(`HTTP ${sectionResult.value.status}`);

      const text = await sectionResult.value.text();
      const parsed = new DOMParser().parseFromString(text, 'text/html');
      const fragment = parsed.querySelector('.alto-search-results') || parsed.body;
      this.showResults(fragment.outerHTML || fragment.innerHTML);

      if (countResult.status === 'fulfilled' && countResult.value.ok) {
        const html = await countResult.value.text();
        const parsedCount = new DOMParser().parseFromString(html, 'text/html');
        const totalEl = parsedCount.querySelector('[data-alto-search-total]');
        if (totalEl) {
          const total = parseInt(totalEl.textContent.trim(), 10);
          if (!isNaN(total)) this.updateViewAllCount(total);
        }
      }
    } catch (error) {
      if (error.name === 'AbortError') return;
      console.error('alto-search: fetch failed', error);
      this.showError();
    }
  }

  updateViewAllCount(count) {
    const link = this.querySelector('[data-alto-search-view-all]');
    if (!link) return;
    const span = link.querySelector('span') || link;
    span.textContent = span.textContent.replace(/\(\d+\)/, `(${count})`);
  }
}

if (!customElements.get('alto-search')) {
  customElements.define('alto-search', AltoSearch);
}
